import type { HiringEventData } from '../hooks/useConditionalHiringEventsData';
import type { ResumeEngagementContextData } from '../hooks/useFindResumeEngagement';
import type { ViewjobOneGraphModel } from '../viewjobOneGraphModel';

import React, { useContext } from 'react';

export type IViewJobDataContext = {
    initialData?: model.viewjob.ViewJobClientSideModel;
    viewjobOneGraphModel?: ViewjobOneGraphModel;
    isDesktopSERP?: boolean;
    trackJobClickOnApply?: boolean;
    jobClickTrackingAttributes?: {};
    isSerpShowingCompanyHeader?: boolean;
    domain: string;
    viewjobSourceUrl: string;
    proctorGroups?: string | null;
    resumeEngagementContext?: ResumeEngagementContextData;
    hiringEventsData?: HiringEventData;
    from?: string | null;
    /**
     * The Indeed Apply link URL, loaded asynchronously after the main viewjob data.
     * This is used by the IndeedApplyButton to construct the apply URL.
     * null means the URL hasn't loaded yet or is not applicable.
     */
    applyLinkUrl?: string | null;
    /**
     * Whether the apply link is currently loading.
     */
    applyLinkLoading?: boolean;
};

export const ViewJobDataContext = React.createContext<IViewJobDataContext>({
    initialData: undefined,
    viewjobOneGraphModel: undefined,
    domain: '',
    viewjobSourceUrl: '',
    proctorGroups: '',
    hiringEventsData: undefined,
    applyLinkUrl: null,
    applyLinkLoading: false,
});

export default ViewJobDataContext;

/**
 * a hook for accessing the view job data context without wrapping the component in `ViewJobDataContext.Provider`
 *
 * @returns the value of the view job context provider
 */
export const useViewJobDataContext = (): IViewJobDataContext => useContext(ViewJobDataContext);

export const useViewjobOneGraphModel = () => useContext(ViewJobDataContext).viewjobOneGraphModel;

export const useDomainUrl = () => useContext(ViewJobDataContext).domain;

export const useViewjobSourceUrl = () => useContext(ViewJobDataContext).viewjobSourceUrl;

export const useProctorGroups = () => useContext(ViewJobDataContext).proctorGroups;

export const useResumeEngagementContextData = () =>
    useContext(ViewJobDataContext).resumeEngagementContext;

export const useHiringEventsData = () => useContext(ViewJobDataContext).hiringEventsData;

export const useApplyLinkUrl = () => useContext(ViewJobDataContext).applyLinkUrl;

export const useApplyLinkLoading = () => useContext(ViewJobDataContext).applyLinkLoading;

/**
 * Hook to determine if the current ViewJob display is for desktop (web).
 * Returns true if rnViewJobDisplay is 'DESKTOP_STANDALONE' or 'DESKTOP_EMBEDDED'.
 * Used to conditionally show/hide UI elements based on desktop vs mobile display.
 */
export const useIsDesktopDisplay = () => {
    const rnViewJobDisplay =
        useContext(ViewJobDataContext).initialData?.rnViewJobDisplay ?? 'MOBILE_STANDALONE';
    return rnViewJobDisplay === 'DESKTOP_STANDALONE' || rnViewJobDisplay === 'DESKTOP_EMBEDDED';
};
